import { useEffect, useMemo, useState } from "react";
import { ClipboardList, Loader2, Undo2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { fetchFulfilmentOrders, applyFulfilmentOrders, unapplyFulfilmentOrder } from "@/lib/api";

const iso = (d) => d.toISOString().slice(0, 10);
const fmt = (n) => `$${Number(n || 0).toFixed(2)}`;
const feeFor = (units, d) => Number(d.shipment_fee || 0) + units * Number(d.per_unit_fee || 0);

function ledgerState(o) {
  if (o.ledger?.mine) return { l: "In ledger", c: "bg-emerald-50 text-emerald-700 border-emerald-200" };
  if (o.ledger) return { l: `Ledger · ${o.ledger.dealer_name || o.ledger.uid}`, c: "bg-amber-50 text-amber-700 border-amber-200" };
  if (["cancelled", "refunded", "failed"].includes(o.status)) return { l: "Cancelled", c: "bg-rose-50 text-rose-600 border-rose-200" };
  return { l: "Not picked", c: "bg-zinc-50 text-zinc-600 border-zinc-200" };
}

export default function FulfilmentOrdersPicker({ caller, uid, dealerName, defaults, onChanged }) {
  const [from, setFrom] = useState(iso(new Date(Date.now() - 30 * 864e5)));
  const [to, setTo] = useState(iso(new Date()));
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sel, setSel] = useState(new Set());
  const [onlyOpen, setOnlyOpen] = useState(true);

  async function load() {
    setLoading(true);
    try {
      const d = await fetchFulfilmentOrders(caller, uid, from, to);
      setOrders(d.orders || []);
      setSel(new Set());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load orders");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { if (caller && uid) load(); /* eslint-disable-next-line */ }, [caller, uid, from, to]);

  const pickable = (o) => !o.ledger && !["cancelled", "refunded", "failed"].includes(o.status);
  const visible = useMemo(() => (onlyOpen ? orders.filter((o) => pickable(o) || o.ledger?.mine) : orders), [orders, onlyOpen]);
  const selectable = visible.filter(pickable);
  const allSel = selectable.length > 0 && selectable.every((o) => sel.has(o.id));
  const selOrders = orders.filter((o) => sel.has(o.id));
  const selUnits = selOrders.reduce((a, o) => a + o.units, 0);
  const selFees = selOrders.reduce((a, o) => a + feeFor(o.units, defaults), 0);

  async function apply() {
    if (!sel.size) return;
    setBusy(true);
    try {
      const r = await applyFulfilmentOrders(caller, uid, [...sel]);
      toast.success(`${r.count} order${r.count === 1 ? "" : "s"} added to ${dealerName || "the dealer"}'s ledger · inventory adjusted`);
      if (r.skipped?.length) toast.warning(`${r.skipped.length} skipped (already in a ledger)`);
      onChanged?.();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add orders");
    } finally {
      setBusy(false);
    }
  }

  async function undo(o) {
    setBusy(true);
    try {
      await unapplyFulfilmentOrder(caller, uid, o.id);
      toast.success(`Order #${o.order_id} removed from ledger · inventory restored`);
      onChanged?.();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not undo");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="rounded-2xl border border-zinc-200 bg-white p-3 sm:p-4 space-y-3" data-testid="ff-pick-orders">
      <div className="flex items-center gap-2">
        <ClipboardList size={16} className="text-violet-600" />
        <h3 className="text-sm font-bold text-zinc-900">Pick orders fulfilled by {dealerName || "this dealer"}</h3>
        <span className="text-[11px] text-zinc-500 ml-auto">{orders.length} orders</span>
      </div>
      <p className="text-[11px] text-zinc-500">Tick the orders this dealer shipped. Each one becomes a fulfilled-order row ({fmt(defaults.shipment_fee)} + {fmt(defaults.per_unit_fee)}/unit) and its units are deducted from Inventory.</p>
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <label className="flex items-center gap-1 text-zinc-600">From <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-8 rounded-md border border-zinc-300 px-1.5" data-testid="ff-orders-from" /></label>
        <label className="flex items-center gap-1 text-zinc-600">To <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-8 rounded-md border border-zinc-300 px-1.5" data-testid="ff-orders-to" /></label>
        <label className="flex items-center gap-1.5 text-zinc-600 ml-auto cursor-pointer">
          <Checkbox checked={onlyOpen} onCheckedChange={(v) => setOnlyOpen(!!v)} data-testid="ff-orders-only-open" /> Hide other ledgers &amp; cancelled
        </label>
      </div>

      <div className="flex items-center justify-between gap-2">
        <label className="flex items-center gap-2 text-xs text-zinc-700 cursor-pointer">
          <Checkbox checked={allSel} onCheckedChange={(v) => setSel(v ? new Set(selectable.map((o) => o.id)) : new Set())} disabled={!selectable.length} data-testid="ff-orders-select-all" />
          Select all ({selectable.length})
        </label>
        <Button size="sm" disabled={busy || sel.size === 0} onClick={apply} className="h-8 rounded-lg bg-violet-600 hover:bg-violet-700" data-testid="ff-orders-apply">
          {busy ? <Loader2 size={14} className="animate-spin" /> : `Add ${sel.size || ""} ${sel.size === 1 ? "order" : "orders"} to ledger${selUnits ? ` · ${selUnits} units · ${fmt(selFees)}` : ""}`}
        </Button>
      </div>

      {loading ? (
        <div className="py-8 flex justify-center"><Loader2 className="animate-spin text-zinc-400" size={18} /></div>
      ) : visible.length === 0 ? (
        <div className="py-6 text-center text-xs text-zinc-500" data-testid="ff-orders-empty">No orders in this range.</div>
      ) : (
        <ul className="divide-y divide-zinc-100 max-h-[420px] overflow-y-auto -mx-1 px-1" data-testid="ff-orders-list">
          {visible.map((o) => {
            const st = ledgerState(o);
            const can = pickable(o);
            return (
              <li key={o.id} className="flex items-start gap-2.5 py-2" data-testid={`ff-order-${o.order_id}`}>
                <div className="pt-0.5">
                  <Checkbox checked={sel.has(o.id)} disabled={!can} onCheckedChange={(v) => setSel((s) => { const n = new Set(s); v ? n.add(o.id) : n.delete(o.id); return n; })} data-testid={`ff-order-check-${o.order_id}`} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-zinc-900">#{o.order_id}</span>
                    <span className="text-xs text-zinc-500">{o.date}</span>
                    <span className="text-xs text-zinc-700 truncate">{o.customer}</span>
                    <span className={`text-[10px] font-semibold uppercase tracking-wide rounded-full border px-1.5 py-0.5 ${st.c}`} data-testid={`ff-order-state-${o.order_id}`}>{st.l}</span>
                    <span className="text-[10px] text-zinc-400 uppercase">{o.status}</span>
                  </div>
                  <div className="text-[11px] text-zinc-500 truncate">
                    {o.lines.map((l) => `${l.qty}× ${l.name}`).join(" · ")}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-xs font-semibold tabular-nums text-zinc-800">{o.units} units</div>
                  <div className="text-[10px] tabular-nums text-violet-700">{fmt(feeFor(o.units, defaults))}</div>
                  {o.ledger?.mine && !(o.ledger.paid_out > 0) && (
                    <button type="button" disabled={busy} onClick={() => undo(o)} className="inline-flex items-center gap-1 text-[10px] text-zinc-500 hover:text-rose-600" data-testid={`ff-order-undo-${o.order_id}`}>
                      <Undo2 size={11} /> undo
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
